import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet } from '../lib/api.js';
import { getSocket } from '../lib/socket.js';
import {
  LayoutDashboard, Users, Inbox, Phone, Calendar,
  Megaphone, PieChart, Settings, ShieldCheck, Wrench, Sparkles, Briefcase, LogOut, Wand2,
  Kanban, Headset, DollarSign, Bot, Shield, PhoneCall, FileSignature, Activity, Mic, CalendarClock, Building2,
  Brain, Stethoscope, Target, Trophy, BarChart2, Workflow, UserCog, ClipboardList, Hash, RefreshCw,
  RotateCcw, PhoneIncoming, ExternalLink, TrendingUp,
  ShoppingCart, MessageSquareText, Tag, Pin, PinOff,
} from 'lucide-react';
import RailTip from './layout/RailTip.jsx';
import { usePersistedFlag } from '../hooks/usePersistedFlag.js';
import Logo from './Logo.jsx';
import MasterSwitch from './MasterSwitch.jsx';
import BillingGate from './BillingGate.jsx';
import PhonePanel from './PhonePanel.jsx';
import InboundCallPopup from './InboundCallPopup.jsx';
import AIDialerScreenPop from './dialer/AIDialerScreenPop.jsx';
import ApprovalWaitingScreen from './onboarding/ApprovalWaitingScreen.jsx';
import AgentBillingSetup from './onboarding/AgentBillingSetup.jsx';
import TrialBanner from './TrialBanner.jsx';
import HeaderCluster from './layout/HeaderCluster.jsx';
import { useAuth } from '../store/auth.js';
import { initials } from '../lib/format.js';
import { useAIDialerEnabled } from '../lib/aiDialerFlag.js';
import { useAITextAgentEnabled } from '../lib/aiTextAgentFlag.js';
import { useA2pBypass } from '../lib/a2pBypassFlag.js';

// Feature flags read from Vite build env. Default to OFF when unset.
const ROLEPLAY_ENABLED = import.meta.env.VITE_ENABLE_ROLEPLAY === 'true';
const LEAD_ORDERS_ENABLED = import.meta.env.VITE_ENABLE_LEAD_ORDERS === 'true';
// Carrier e-Apps sidebar entry + /carriers page. Off by default: as of
// 2026-07-07 we don't have integration permission from the carriers, so the
// launcher can't be trusted end-to-end. Flip VITE_ENABLE_CARRIER_EAPPS=true
// at build time if/when carrier approvals land. Route stays wired (see
// App.jsx) but redirects to /dashboard when the flag is off, so a direct
// visit doesn't hit an orphan page.
const CARRIER_EAPPS_ENABLED = import.meta.env.VITE_ENABLE_CARRIER_EAPPS === 'true';

// Sidebar with section headers + open items beneath them.
// Sections render their header (text only) followed by their items inline.
//
// Built as a function so per-render flags (e.g. the runtime AI Dialer tenant
// flag) can be injected. Pass `{ aiDialerEnabled, aiTextAgentEnabled }` to
// gate the corresponding sidebar entries.
const buildSections = ({ aiDialerEnabled, aiTextAgentEnabled, hideA2pUI }) => [
  {
    label: 'Workspace',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, end: true },
      { to: '/leads', label: 'Leads', icon: Users },
      { to: '/inbox', label: 'Inbox', icon: Inbox },
      { to: '/pipeline', label: 'Sales Pipeline', icon: Kanban },
      { to: '/calendar', label: 'Calendar', icon: Calendar },
    ],
  },
  {
    label: 'Dialing',
    items: [
      { to: '/phone-setup', label: 'Phone Setup', icon: Phone },
      { to: '/calls', label: 'Call History', icon: PhoneCall },
      // Inbound Routing hidden from nav (route stays mounted for direct links).
      // External white-label booth — opens in a new tab.
      { href: 'https://inbounds.civixcrm.ai/booth', label: 'Inbound Calls', icon: PhoneIncoming, external: true, badge: 'Live' },
      // Business Profile is the A2P registration entry point. Hidden for
      // tenants on the parent CIVIX Brand (features.a2p.hideUI) — there's no
      // registration for them to do.
      ...(hideA2pUI ? [] : [{ to: '/business-profile', label: 'Business Profile', icon: Building2 }]),
      // DNC & Compliance tab hidden 2026-07-06 pending redesign — CIVIX does
      // not scrub against National DNC / state DNC / RND / litigator lists,
      // and the Blacklist Alliance vendor connection was never provisioned.
      // Leaving the /dnc-compliance route reachable by direct URL for the
      // internal DNC list (which still works), but removed from nav so agents
      // don't rely on platform-side scrubbing that isn't happening. See
      // ai_compliance_shield.md §10 for the defensive positioning rationale.
      // { to: '/dnc-compliance', label: 'DNC & Compliance', icon: Shield },
      // Voice Studio / Bots / Bot Sync Requests are now reachable from the
      // unified AI Dialer page tabs — see AI Tools section.
    ],
  },
  {
    label: 'AI Tools',
    items: [
      { to: '/civix-assistant', label: 'CIVIX Assistant', icon: Brain },
      // Unified AI Dialer hub. Gated on the runtime per-tenant flag (which
      // also returns true when VITE_ENABLE_AI_DIALER is set at build time).
      ...(aiDialerEnabled ? [{ to: '/ai-dialer', label: 'AI Dialer', icon: Bot }] : []),
      // AI Text Agent — sidebar-only scaffold pending route implementation.
      // Route target `/ai-text-agent` is intentionally not yet wired in
      // web/src/App.jsx; enabling the flag before the route lands will render
      // a broken link. Gate is default-OFF (no build flag, no tenant feature)
      // until the surface is ready.
      ...(aiTextAgentEnabled ? [{ to: '/ai-text-agent', label: 'AI Text Agent', icon: MessageSquareText }] : []),
      ...(ROLEPLAY_ENABLED ? [{ to: '/roleplay', label: 'AI Roleplay', icon: Mic }] : []),
      { to: '/underwriting-bot', label: 'Underwriting Bot', icon: Stethoscope },
      // Shadow Mode is now a tab inside AI Dialer (/ai-dialer?tab=shadow-mode).
      // Persistency AI is hidden from nav — backend runs silently; /persistency
      // is still reachable by direct URL.
    ],
  },
  {
    label: 'Growth',
    items: [
      // Marketing Hub is the canonical home for preset texts, drips, automations,
      // dispositions, keywords, preset replies, voicemails, webhooks (all as tabs).
      { to: '/marketing', label: 'Marketing Hub', icon: Megaphone },
      // Lead Vendors is the canonical lead-source manager (formerly duplicated by Campaigns).
      { to: '/vendors', label: 'Lead Vendors', icon: Briefcase },
      // Sidebar consolidation (PR-A): Second Chance page deleted (dup of Aged
      // Leads). "Aged Leads" renamed to "Lead Pool" — same route/backend,
      // clearer label since lead-assist agents work THIS pool.
      { to: '/aged-leads', label: 'Lead Pool', icon: RotateCcw },
      { to: '/sync-account', label: 'Sync Account', icon: RefreshCw },
      // Carrier e-Apps — hidden by default (no integration permission yet).
      // See CARRIER_EAPPS_ENABLED comment at top of file.
      ...(CARRIER_EAPPS_ENABLED
        ? [{ to: '/carriers', label: 'Carrier e-Apps', icon: FileSignature }]
        : []),
    ],
  },
  {
    label: 'Performance',
    items: [
      { to: '/reports', label: 'Reports', icon: PieChart },
      { to: '/growth/lead-roi', label: 'Lead ROI', icon: TrendingUp },
      { to: '/leaderboard', label: 'Leaderboard', icon: Trophy },
      { to: '/digest', label: 'My Digest', icon: BarChart2 },
      { to: '/ai-usage', label: 'AI Usage', icon: Workflow },
    ],
  },
  {
    label: 'Admin',
    adminOnly: true,
    items: [
      { to: '/admin', label: 'Overwatch', icon: ShieldCheck, adminOnly: true },
      { to: '/admin-aged-leads', label: 'Lead Pool Mgmt', icon: RotateCcw, adminOnly: true },
      { to: '/lead-distribution', label: 'Lead Distribution', icon: Workflow, adminOnly: true },
      // Status Groups: hidden from sidebar 2026-07-07 — Phase-3 feature whose
      // consumer side (reports/dashboards filtering by group) was never built,
      // so the standalone page adds noise. Route + API stay mounted so vendor
      // form's optional group picker and any direct links still work.
      // { to: '/lead-status-groups', label: 'Status Groups', icon: Hash, adminOnly: true },
      { to: '/lead-roi', label: 'Lead ROI Engine', icon: Sparkles, adminOnly: true },
      // Lead Orders is hidden by default — surface under Admin only when the
      // feature flag is on. The /lead-orders route is always mounted so direct
      // links still work, but it's omitted from nav otherwise.
      ...(LEAD_ORDERS_ENABLED
        ? [{ to: '/lead-orders', label: 'Lead Orders', icon: ShoppingCart, adminOnly: true }]
        : []),
      { to: '/devtools', label: 'DevTools', icon: Wrench, superadminOnly: true },
    ],
  },
  {
    label: 'Account',
    items: [
      { to: '/settings', label: 'Settings', icon: Settings },
      // Booking Page lives under Settings ➔ Booking Page only (no standalone nav entry).
      { to: '/settings/scheduler', label: 'Scheduler', icon: CalendarClock },
      { to: '/billing', label: 'Billing', icon: DollarSign },
      { to: '/support', label: 'Support', icon: Headset },
      { to: '/status', label: 'System Status', icon: Activity },
    ],
  },
];

export default function Layout() {
  const qc = useQueryClient();
  const { agent, logout, isAdmin, isSuperadmin } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const settingsTab = new URLSearchParams(loc.search).get('tab');
  // Pinned = always expanded and pushes content. Unpinned = compact rail that floats open while hovered.
  const [pinned, togglePinned] = usePersistedFlag('civix_sidebar_pinned', false);
  const [hovered, setHovered] = useState(false);
  const collapsed = !(pinned || hovered);
  const { enabled: aiDialerEnabled, loading: aiDialerLoading } = useAIDialerEnabled();
  const { enabled: aiTextAgentEnabled, loading: aiTextAgentLoading } = useAITextAgentEnabled();
  const { hideUI: hideA2pUI } = useA2pBypass();
  // While the runtime flags are still loading, treat entries as off so we
  // don't flash them into the sidebar and then yank them.
  const SECTIONS = buildSections({
    aiDialerEnabled: aiDialerEnabled && !aiDialerLoading,
    aiTextAgentEnabled: aiTextAgentEnabled && !aiTextAgentLoading,
    hideA2pUI,
  });

  // Pending-teammate count for the "My Team" badge. Shares the query key used by
  // Dashboard/Team, so approving/rejecting anywhere updates the badge instantly;
  // the socket events cover signups and approvals made in other sessions.
  const canApprove = !!agent && agent.status !== 'pending_approval' && isAdmin();
  const { data: pendingData } = useQuery({
    queryKey: ['pending-teammate-approvals'],
    queryFn: () => apiGet('/team/pending-approvals'),
    enabled: canApprove,
  });
  const pendingApprovalsCount =
    pendingData?.pendingApprovalCount ?? (pendingData?.items || pendingData?.pendingAgents || []).length;

  // Pending Account Sync requests addressed to this user (managers/admins).
  const { data: syncPendingData } = useQuery({
    queryKey: ['team-sync-requests'],
    queryFn: () => apiGet('/sync/pending'),
    enabled: !!agent && agent.status !== 'pending_approval',
  });
  const pendingSyncCount = (syncPendingData?.requests || []).length;
  const navBadges = {
    '/settings': pendingApprovalsCount,
    '/settings?tab=team': pendingApprovalsCount,
    '/sync-account': pendingSyncCount,
  };

  useEffect(() => {
    if (!agent || agent.status === 'pending_approval') return undefined;
    const socket = getSocket();
    if (!socket?.on) return undefined;
    const refresh = () => qc.invalidateQueries({ queryKey: ['team-sync-requests'] });
    const events = ['sync:requested', 'sync:completed', 'sync:rejected', 'sync:pending_count_updated'];
    events.forEach((e) => socket.on(e, refresh));
    return () => events.forEach((e) => socket.off(e, refresh));
  }, [agent, qc]);

  useEffect(() => {
    if (!canApprove) return undefined;
    const socket = getSocket();
    if (!socket?.on) return undefined;
    const refresh = () => qc.invalidateQueries({ queryKey: ['pending-teammate-approvals'] });
    // The same registration is emitted to the manager, tenant and admin rooms, so
    // a client can receive it up to 3 times: a per-agent toast id collapses them.
    const onRegistered = (payload) => {
      const who = payload?.agent?.name || payload?.agent?.email || 'A teammate';
      toast(`🔔 ${who} registered and is pending your approval`, { id: `registered-${payload?.agent?._id || who}`, duration: 6000 });
      refresh();
    };
    socket.on('team:agent-registered', onRegistered);
    socket.on('team:pending_count_updated', refresh);
    return () => {
      socket.off('team:agent-registered', onRegistered);
      socket.off('team:pending_count_updated', refresh);
    };
  }, [canApprove, qc]);

  if (agent?.status === 'pending_approval') {
    return (
      <div className="min-h-screen bg-civix-bg flex items-center justify-center p-4">
        <ApprovalWaitingScreen />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-civix-bg">
      {/* Reserves the rail's space by PIN state only, so hovering never reflows the page. */}
      <div className={`shrink-0 transition-all duration-300 ease-in-out ${pinned ? 'w-[17rem]' : 'w-20'}`} aria-hidden="true" />
      <aside
        data-collapsed={collapsed}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={`${collapsed ? 'w-16' : 'w-64'} ${!pinned && hovered ? 'shadow-2xl backdrop-blur-md bg-civix-panel/95 border-civix-purple/30' : ''} fixed top-4 left-4 panel flex flex-col h-[calc(100vh-2rem)] z-40 overflow-hidden transition-all duration-300 ease-in-out`}
      >
        <div className={`border-b border-civix-border/60 flex items-center min-h-[3.5rem] ${collapsed ? 'justify-center p-2' : 'justify-between p-5'}`}>
          {!collapsed && <Logo size="md" />}
          {!collapsed && (
            <button
              type="button"
              onClick={togglePinned}
              aria-pressed={pinned}
              aria-label={pinned ? 'Unpin sidebar' : 'Pin sidebar open'}
              title={pinned ? 'Unpin sidebar (auto-retract on hover out)' : 'Pin sidebar open'}
              data-testid="sidebar-toggle"
              className="p-1.5 rounded-md text-white/50 hover:text-white hover:bg-civix-panelLight transition"
            >
              {pinned ? <Pin className="w-4 h-4 text-civix-purpleSoft" /> : <PinOff className="w-4 h-4" />}
            </button>
          )}
        </div>

        <nav className={`flex-1 overflow-y-auto overflow-x-hidden py-4 space-y-4 ${collapsed ? 'px-2' : 'px-3'}`}>
          {SECTIONS.map((section, sIdx) => {
            // Hide whole section if it's admin-only and user isn't admin
            if (section.adminOnly && !isAdmin()) return null;

            // Filter items by role
            const visibleItems = section.items.filter((item) => {
              if (item.adminOnly && !isAdmin()) return false;
              if (item.superadminOnly && !isSuperadmin()) return false;
              return true;
            });
            if (visibleItems.length === 0) return null;

            return (
              <div key={section.label} className="space-y-1">
                <div className={`${collapsed ? '' : 'px-3'} ${sIdx === 0 ? '' : 'pt-2 mt-1 border-t border-civix-border/40'}`}>
                  {!collapsed && (
                    <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-civix-violet/60 pt-2 pb-1">
                      {section.label}
                    </div>
                  )}
                </div>
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  if (item.external || item.href) {
                    return (
                      <RailTip key={item.href} label={item.badge ? `${item.label} · ${item.badge}` : item.label} enabled={collapsed}>
                        <a
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={item.label}
                          className={`relative flex items-center gap-3 py-2 rounded-lg text-sm font-medium transition-all text-white/60 hover:text-white hover:bg-civix-panelLight ${collapsed ? 'justify-center px-0' : 'px-3'}`}
                        >
                          <Icon className="w-4 h-4 shrink-0" />
                          {!collapsed && <span className="truncate">{item.label}</span>}
                          {!collapsed && item.badge && (
                            <span className="ml-auto px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-semibold">
                              {item.badge}
                            </span>
                          )}
                          {!collapsed && <ExternalLink className={`w-3 h-3 text-white/40 ${item.badge ? '' : 'ml-auto'}`} />}
                          {collapsed && item.badge && <span className="absolute top-1 right-2 w-2 h-2 rounded-full bg-emerald-400" />}
                        </a>
                      </RailTip>
                    );
                  }
                  const pending = navBadges[item.to] || 0;
                  return (
                    <RailTip key={item.to} label={pending > 0 ? `${item.label} · ${pending} pending` : item.label} enabled={collapsed}>
                    <NavLink
                      aria-label={item.label}
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      onMouseEnter={() => {
                        if (item.to === '/calls') {
                          qc.prefetchQuery({
                            queryKey: ['call-history', { page: 1, limit: 50 }],
                            queryFn: () => apiGet('/phone/calls', { page: 1, limit: 50 }),
                            staleTime: 0,
                          });
                        }
                      }}
                      onClick={() => {
                        if (item.to === '/calls') {
                          qc.invalidateQueries({ queryKey: ['call-history'] });
                        }
                      }}
                      className={({ isActive: pathActive }) => {
                        // Query-string items (My Team) and plain /settings share a
                        // pathname, so disambiguate by the ?tab= value.
                        const isActive = item.to === '/settings?tab=team'
                          ? loc.pathname === '/settings' && settingsTab === 'team'
                          : item.to === '/settings'
                            ? pathActive && settingsTab !== 'team'
                            : pathActive;
                        return `relative flex items-center gap-3 py-2 rounded-lg text-sm font-medium transition-all ${collapsed ? 'justify-center px-0' : 'px-3'} ${
                          isActive
                            ? 'bg-civix-purple/20 text-white shadow-glow-purple border border-civix-purple/40'
                            : 'text-white/60 hover:text-white hover:bg-civix-panelLight'
                        }`;
                      }}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                      {pending > 0 && (
                        collapsed ? (
                          <span className="absolute top-0.5 right-1.5 w-2.5 h-2.5 rounded-full bg-rose-600 ring-2 ring-civix-bg animate-pulse" aria-label={`${pending} pending`} />
                        ) : (
                          <span
                            className="ml-auto flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-semibold leading-none text-white bg-rose-600 rounded-full shadow-sm ring-2 ring-civix-bg animate-pulse"
                            aria-label={`${pending} pending`}
                          >
                            {pending > 9 ? '9+' : pending}
                          </span>
                        )
                      )}
                    </NavLink>
                    </RailTip>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="p-3 border-t border-civix-border/60 shrink-0 mt-auto">
          <RailTip label={`${agent?.name || 'Account'} · Settings`} enabled={collapsed}>
          <div
            className={`group flex items-center gap-3 py-2 rounded-lg hover:bg-civix-panelLight transition cursor-pointer ${collapsed ? 'justify-center px-0' : 'px-2'}`}
            onClick={() => nav('/settings')}
          >
            <div className="w-8 h-8 shrink-0 rounded-full overflow-hidden bg-civix-gradient flex items-center justify-center text-xs font-bold">
              {agent?.avatar
                ? <img src={agent.avatar} alt={agent.name || ''} className="w-full h-full object-cover"/>
                : initials(agent?.name)}
            </div>
            {!collapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{agent?.name}</div>
                  <div className="text-xs text-white/50 capitalize">{agent?.role}</div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); logout(); nav('/login'); }}
                  className="flex items-center gap-1.5 px-2 py-1 rounded text-white/50 hover:text-rose-400 hover:bg-white/5 transition"
                  title="Sign out"
                  aria-label="Sign out"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-xs font-medium hidden group-hover:inline">Sign Out</span>
                </button>
              </>
            )}
          </div>
          </RailTip>
          {collapsed && (
            <RailTip label="Sign out" className="mt-1">
              <button
                onClick={() => { logout(); nav('/login'); }}
                className="w-full flex justify-center py-2 rounded-lg text-white/50 hover:text-rose-400 hover:bg-white/5 transition"
                aria-label="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </RailTip>
          )}
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-6 overflow-x-clip">
        <TrialBanner />
        <BillingGate />
        {/* Global header: tenant Master Switch on the left, availability · bell · Welcome menu · phone on the right. */}
        <div className="sticky top-0 z-30 -mx-6 -mt-6 mb-4 px-6 py-3 flex items-start gap-3 bg-civix-bg/90 backdrop-blur-sm">
          <div className="flex-1 min-w-0 [&>div]:mb-0"><MasterSwitch /></div>
          <HeaderCluster />
        </div>
        {import.meta.env.VITE_DEMO_MODE === 'true' && (
          <div className="mb-4 panel px-4 py-2 flex items-center gap-3 text-xs">
            <span className="px-2 py-0.5 rounded-full bg-civix-purple/20 text-civix-purple font-semibold tracking-wide">DEMO</span>
            <span className="text-white/70">Live preview running on in-memory data — explore everything. The full source ships with Docker Compose, MongoDB, Express API, and Socket.IO.</span>
          </div>
        )}
        <Outlet />
      </main>

      {/* Persistent softphone — floats on every page (top-right toggle + slide-out panel) */}
      <PhonePanel />

      {/* Global inbound-call popups — listens on socket 'inbound:call' from
          tracking-number routes (e.g. 877-735-8644). Stacks toasts top-right. */}
      <InboundCallPopup />

      {/* AI Dialer screen-pop — global drawer that fires when the hand-off
          engine emits ai-dialer:screen-pop for this agent. Self-gated on
          the runtime per-tenant flag so it's a no-op when the flag is off. */}
      <AIDialerScreenPop />
    </div>
  );
}
